import { useEffect } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Skeleton from '@mui/material/Skeleton'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { selectUseEventsManagerDashboard } from 'src/v3/settings/settings.slice'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import {
  activeEventTabSet,
  fetchManageEventAccreditationJoins,
  fetchManageEventAccreditations,
  fetchManageEvents,
  selectActiveEventTab,
  selectManageEventAccreditationJoinsStatus,
  selectManageEventAccreditationsStatus,
  selectManageEventById,
  selectManageEventsStatus,
  sessionsReset,
} from './manage-events.slice'
import EventDetailHeader from './components/event-detail-header'
import AttendeesTab from './tabs/attendees-tab'
import CapacityBreakdownTab from './tabs/capacity-breakdown-tab'
import EventDetailsTab from './tabs/event-details-tab'
import SessionsTab from './tabs/sessions-tab'

let TAB_LABELS = [
  'Event Details',
  'Sessions',
  'Attendees',
  'Capacity Breakdown',
]

type TabPanelProps = {
  children: React.ReactNode
  value: number
  index: number
}

let TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`event-detail-tabpanel-${index}`}
    aria-labelledby={`event-detail-tab-${index}`}
  >
    {value === index && <Box sx={{ mt: 0.75 }}>{children}</Box>}
  </div>
)

let ManageEventDetailContent = () => {
  let { eventID } = useParams<{ eventID: string }>()
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let eventsStatus = useAppSelector(selectManageEventsStatus)
  let accreditationsStatus = useAppSelector(
    selectManageEventAccreditationsStatus,
  )
  let accreditationJoinsStatus = useAppSelector(
    selectManageEventAccreditationJoinsStatus,
  )
  let event = useAppSelector(selectManageEventById(eventID ?? ''))
  let tabIndex = useAppSelector(selectActiveEventTab)

  useScrollToTop()

  useAutoFetch(eventsStatus, fetchManageEvents)
  useAutoFetch(accreditationsStatus, fetchManageEventAccreditations)
  useAutoFetch(accreditationJoinsStatus, fetchManageEventAccreditationJoins)

  useEffect(() => {
    dispatch(sessionsReset())
    dispatch(activeEventTabSet(0))
  }, [eventID, dispatch])

  let handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    dispatch(activeEventTabSet(newValue))
  }

  let loading =
    eventsStatus === 'pending' ||
    eventsStatus === 'uninitialized' ||
    accreditationsStatus === 'pending' ||
    accreditationsStatus === 'uninitialized' ||
    accreditationJoinsStatus === 'pending' ||
    accreditationJoinsStatus === 'uninitialized'

  let failed =
    eventsStatus === 'rejected' ||
    accreditationsStatus === 'rejected' ||
    accreditationJoinsStatus === 'rejected' ||
    (!loading && !event)

  return (
    <Box className="pb-[84px]">
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        <Box className="flex flex-col gap-4">
          {failed ? (
            <Typography color="error">Failed to load event.</Typography>
          ) : loading ? (
            <>
              <Skeleton variant="text" width={320} height={40} />
              <Skeleton variant="rectangular" height={48} />
            </>
          ) : (
            <EventDetailHeader event={event!} />
          )}

          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="Event detail tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {TAB_LABELS.map((label, i) => (
              <Tab
                key={label}
                label={label}
                id={`event-detail-tab-${i}`}
                aria-controls={`event-detail-tabpanel-${i}`}
              />
            ))}
          </Tabs>

          {failed ? null : loading || !event ? (
            <Skeleton variant="rectangular" height={300} className="rounded" />
          ) : (
            <>
              <TabPanel value={tabIndex} index={0}>
                <EventDetailsTab event={event} />
              </TabPanel>

              <TabPanel value={tabIndex} index={1}>
                <SessionsTab eventID={eventID!} />
              </TabPanel>

              <TabPanel value={tabIndex} index={2}>
                <AttendeesTab eventID={eventID!} />
              </TabPanel>

              <TabPanel value={tabIndex} index={3}>
                <CapacityBreakdownTab eventID={eventID!} />
              </TabPanel>
            </>
          )}
        </Box>
      </Container>

      <Box
        className="fixed bottom-0 left-0 right-0 flex items-center justify-end gap-3 px-8 py-4 border-t"
        sx={{
          zIndex: (theme) => theme.zIndex.appBar,
          background: 'rgba(0,0,0,0.72)',
          backdropFilter: 'blur(8px)',
          borderColor: 'rgba(255,255,255,0.12)',
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/manage/events')}
        >
          Back to Events
        </Button>
        {eventID && (
          <Button
            variant="outlined"
            component={Link}
            to={`/events/${eventID}/edit`}
          >
            Edit event
          </Button>
        )}
      </Box>
    </Box>
  )
}

let ManageEventDetailPage = () => {
  let useEventsManagerDashboard = useAppSelector(
    selectUseEventsManagerDashboard,
  )

  if (!useEventsManagerDashboard) {
    return <Navigate to="/manage" replace />
  }

  return <ManageEventDetailContent />
}

export default ManageEventDetailPage
